import { Component, inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';
import { getMultiFactorResolver } from '@angular/fire/auth';
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ThinkingOrbComponent],
  template: `
    <div class="min-h-screen flex flex-col md:flex-row bg-zaxis-dark text-white relative overflow-hidden">
      <!-- Grid background globally -->
      <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)] pointer-events-none opacity-20 z-0"></div>
      
      <!-- Lado Izquierdo: Visualización Retro (Orbe Gigante y Logs Falsos) -->
      <div class="hidden md:flex flex-1 relative z-0 flex-col items-center justify-center p-8 overflow-hidden">
        
        <!-- Orbe 3D Pixelado -->
        <div class="relative z-10 flex items-center justify-center transform scale-150 opacity-80 mix-blend-screen pointer-events-none">
          <app-thinking-orb state="working" [size]="500" class="text-zaxis-accent"></app-thinking-orb>
        </div>
        
        <!-- Texto críptico superpuesto -->
        <div class="absolute bottom-8 left-8 text-left font-mono text-[10px] text-zaxis-accent/50 uppercase tracking-widest z-20 pointer-events-none">
          <p>MOTOR DE TELEMETRÍA ZAXIS</p>
          <p>ANÁLISIS DE SESIONES XR EN LÍNEA</p>
          <p class="animate-pulse mt-2 text-zaxis-accent/80">ESPERANDO AUTENTICACIÓN SECURE_TLS</p>
        </div>
      </div>

      <!-- Lado Derecho: Formulario -->
      <div class="relative z-10 w-full md:w-[420px] flex-shrink-0 flex flex-col justify-center px-12 md:px-12 bg-black border-l border-zaxis-border shadow-pixel-purple">
        <!-- Decoración superior estilo terminal -->
        <div class="absolute top-0 left-0 w-full h-1 bg-zaxis-accent"></div>
        
        <div class="mb-12">
          <h1 class="text-4xl font-black tracking-tight text-white uppercase">Kairos<br>Zaxis</h1>
          <p class="text-xs text-zaxis-accent mt-2 font-mono uppercase tracking-widest leading-relaxed">Autenticación de<br>Telemetría</p>
        </div>

        <form (ngSubmit)="onSubmit()" class="space-y-8 w-full">
          <ng-container *ngIf="!mfaRequired">
          <div>
            <label class="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Usuario / Correo</label>
            <div class="relative w-full bg-black border transition-colors"
                 [class.border-zaxis-border]="!emailFocused"
                 [class.border-transparent]="emailFocused"
                 [class.ring-1]="emailFocused"
                 [class.ring-zaxis-accent]="emailFocused">
              <input 
                type="text" 
                id="email"
                name="email" 
                [(ngModel)]="email" 
                (focus)="emailFocused = true; updateEmailCursor($event)"
                (blur)="emailFocused = false"
                (keyup)="updateEmailCursor($event)"
                (click)="updateEmailCursor($event)"
                (input)="updateEmailCursor($event)"
                (keydown)="updateEmailCursor($event)"
                required
                class="absolute inset-0 w-full h-full text-transparent bg-transparent outline-none z-20 cursor-text"
              >
              <div class="relative z-10 w-full px-4 py-4 flex items-center font-mono text-sm whitespace-pre overflow-hidden pointer-events-none">
                <ng-container *ngIf="email || emailFocused">
                  <span class="text-cyan-400">{{ email.slice(0, emailCursorPos) }}</span>
                  <span class="relative">
                    <span *ngIf="emailFocused" class="absolute left-0 animate-pulse-pixel text-cyan-400">_</span>
                    <span class="text-cyan-400">{{ email.slice(emailCursorPos, emailCursorPos + 1) || ' ' }}</span>
                  </span>
                  <span class="text-cyan-400">{{ email.slice(emailCursorPos + 1) }}</span>
                </ng-container>
                <span *ngIf="!email && !emailFocused" class="text-gray-700">admin&#64;kairos.local</span>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Clave de Acceso</label>
            <div class="relative w-full bg-black border transition-colors"
                 [class.border-zaxis-border]="!passFocused"
                 [class.border-transparent]="passFocused"
                 [class.ring-1]="passFocused"
                 [class.ring-zaxis-accent]="passFocused">
              <input 
                type="password" 
                id="password" 
                name="password" 
                [(ngModel)]="password"
                (focus)="passFocused = true; updatePassCursor($event)"
                (blur)="passFocused = false"
                (keyup)="updatePassCursor($event)"
                (click)="updatePassCursor($event)"
                (input)="updatePassCursor($event)"
                (keydown)="updatePassCursor($event)"
                required
                class="absolute inset-0 w-full h-full text-transparent bg-transparent outline-none z-20 cursor-text"
              >
              <div class="relative z-10 w-full px-4 py-4 flex items-center font-mono text-sm whitespace-pre overflow-hidden pointer-events-none">
                <ng-container *ngIf="password || passFocused">
                  <span class="text-cyan-400 tracking-[0.2em]">{{ maskedPassword.slice(0, passCursorPos) }}</span>
                  <span class="relative">
                    <span *ngIf="passFocused" class="absolute left-0 animate-pulse-pixel text-cyan-400">_</span>
                    <span class="text-cyan-400 tracking-[0.2em]">{{ maskedPassword.slice(passCursorPos, passCursorPos + 1) || ' ' }}</span>
                  </span>
                  <span class="text-cyan-400 tracking-[0.2em]">{{ maskedPassword.slice(passCursorPos + 1) }}</span>
                </ng-container>
                <span *ngIf="!password && !passFocused" class="text-gray-700 tracking-[0.2em]">********</span>
              </div>
            </div>
          </div>
          </ng-container>

          <ng-container *ngIf="mfaRequired">
          <div>
            <label class="block text-[10px] font-mono uppercase tracking-widest text-zaxis-accent mb-2">Código de Autenticación 2FA</label>
            <div class="relative w-full bg-black border transition-colors"
                 [class.border-zaxis-border]="!mfaFocused"
                 [class.border-transparent]="mfaFocused"
                 [class.ring-1]="mfaFocused"
                 [class.ring-zaxis-accent]="mfaFocused">
              <input 
                type="text" 
                id="mfaCode" 
                name="mfaCode" 
                [(ngModel)]="mfaCode"
                (focus)="mfaFocused = true; updateMfaCursor($event)"
                (blur)="mfaFocused = false"
                (keyup)="updateMfaCursor($event)"
                (click)="updateMfaCursor($event)"
                (input)="updateMfaCursor($event)"
                (keydown)="updateMfaCursor($event)"
                maxlength="6"
                required
                class="absolute inset-0 w-full h-full opacity-0 z-20 cursor-text"
              >
              <div class="relative z-10 w-full px-4 py-4 flex items-center font-mono text-sm whitespace-pre overflow-hidden pointer-events-none">
                <ng-container *ngIf="mfaCode || mfaFocused">
                  <span class="text-cyan-400 tracking-[0.2em]">{{ mfaCode.slice(0, mfaCursorPos) }}</span>
                  <span class="relative">
                    <span *ngIf="mfaFocused" class="absolute left-0 animate-pulse-pixel text-cyan-400">_</span>
                    <span class="text-cyan-400 tracking-[0.2em]">{{ mfaCode.slice(mfaCursorPos, mfaCursorPos + 1) || ' ' }}</span>
                  </span>
                  <span class="text-cyan-400 tracking-[0.2em]">{{ mfaCode.slice(mfaCursorPos + 1) }}</span>
                </ng-container>
                <span *ngIf="!mfaCode && !mfaFocused" class="text-gray-700 tracking-[0.2em]">000000</span>
              </div>
            </div>
            <p class="text-[9px] text-gray-500 font-mono uppercase tracking-widest mt-2">Introduce el código generado por tu app autenticadora.</p>
          </div>
          </ng-container>

          <div *ngIf="error" class="text-zaxis-danger text-[11px] font-mono uppercase tracking-widest bg-zaxis-danger/10 p-4 border border-zaxis-danger/20 rounded-none">
            {{ error }}
          </div>
          
          <div *ngIf="success" class="text-zaxis-accent text-[11px] font-mono uppercase tracking-widest bg-zaxis-accent/10 p-4 border border-zaxis-accent/20 rounded-none">
            {{ success }}
          </div>

          <div id="recaptcha-container"></div>
          
          <button 
            type="submit" 
            [disabled]="loading || resetting"
            class="w-full bg-zaxis-card hover:bg-zaxis-accent hover:text-black text-zaxis-accent font-mono uppercase tracking-widest py-4 border border-zaxis-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed rounded-none mt-6"
          >
            <span *ngIf="!loading && !mfaRequired">Iniciar Sesión</span>
            <span *ngIf="!loading && mfaRequired">Verificar Código</span>
            <span *ngIf="loading" class="animate-pulse">Autenticando...</span>
          </button>
          
          <button 
            *ngIf="!mfaRequired"
            type="button" 
            (click)="onResetPassword()"
            [disabled]="loading || resetting"
            class="w-full bg-transparent hover:text-zaxis-accent text-gray-500 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 pt-4"
          >
            <span *ngIf="!resetting">¿Olvidaste tu Clave de Acceso?</span>
            <span *ngIf="resetting" class="animate-pulse">Solicitando...</span>
          </button>
        </form>
      </div>
    </div>
  `
})
export class LoginComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  router = inject(Router);
  platformId = inject(PLATFORM_ID);

  email = '';
  password = '';
  error = '';
  success = '';
  loading = false;
  resetting = false;
  passFocused = false;
  emailFocused = false;
  mfaFocused = false;
  emailCursorPos = 0;
  passCursorPos = 0;
  mfaCursorPos = 0;
  
  mfaRequired = false;
  mfaCode = '';
  mfaResolver: any = null;
  recaptchaVerifier: any;
  isTestBot = false;

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const urlParams = new URLSearchParams(window.location.search);
      const testToken = urlParams.get('test_token');
      if (testToken) {
        this.isTestBot = await this.auth.checkBotBypass(testToken);
      }
      
      if (!this.isTestBot) {
        this.recaptchaVerifier = await this.auth.createInvisibleRecaptcha('recaptcha-container');
        this.recaptchaVerifier.render();
      }
    }
  }

  ngOnDestroy() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }
  }

  updateEmailCursor(event: any) {
    setTimeout(() => {
      this.emailCursorPos = event.target.selectionStart || 0;
    });
  }

  updatePassCursor(event: any) {
    setTimeout(() => {
      this.passCursorPos = event.target.selectionStart || 0;
    });
  }

  updateMfaCursor(event: any) {
    setTimeout(() => {
      this.mfaCursorPos = event.target.selectionStart || 0;
    });
  }

  get maskedPassword() {
    return '*'.repeat(this.password?.length || 0);
  }

  async onSubmit() {
    if (this.mfaRequired) {
      return this.submitMfa();
    }

    if (!this.email || !this.password) {
      this.error = 'ERROR: SE REQUIERE USUARIO Y CLAVE DE ACCESO';
      return;
    }

    try {
      this.loading = true;
      this.error = '';
      this.success = '';
      
      // Verify invisible captcha first unless test bot
      if (this.recaptchaVerifier) {
        await this.recaptchaVerifier.verify();
      }
      
      const res = await this.auth.login(this.email, this.password);
      if (res && res.mfaRequired) {
        this.mfaResolver = res.resolver;
        this.mfaRequired = true;
        this.error = '';
        return;
      }
      this.router.navigate(['/dashboard']);
    } catch (err: any) {
      if (err.code === 'auth/multi-factor-auth-required') {
        // Fallback in case auth.login throws it instead of returning it
        const { getMultiFactorResolver } = await import('@angular/fire/auth');
        this.mfaResolver = getMultiFactorResolver(this.auth.getAuthInstance(), err);
        this.mfaRequired = true;
        this.error = '';
      } else {
        this.error = this.mapAuthError(err);
      }
    } finally {
      this.loading = false;
    }
  }

  async submitMfa() {
    if (!this.mfaCode || this.mfaCode.length < 6) {
      this.error = 'ERROR: INGRESE EL CÓDIGO DE 6 DÍGITOS';
      return;
    }
    try {
      this.loading = true;
      this.error = '';
      await this.auth.resolveMfaSignIn(this.mfaResolver, this.mfaCode);
      this.router.navigate(['/dashboard']);
    } catch (err: any) {
      this.error = 'CÓDIGO 2FA INVÁLIDO O EXPIRADO';
    } finally {
      this.loading = false;
    }
  }

  async onResetPassword() {
    if (!this.email) {
      this.error = 'ERROR: INGRESE USUARIO/CORREO PARA RESTABLECER CLAVE';
      this.success = '';
      return;
    }

    try {
      this.resetting = true;
      this.error = '';
      this.success = '';
      await this.auth.resetPassword(this.email);
      this.success = 'SECUENCIA DE RECUPERACIÓN INICIADA. REVISE SU BANDEJA DE ENTRADA.';
    } catch (err: any) {
      this.error = this.mapAuthError(err);
    } finally {
      this.resetting = false;
    }
  }

  private mapAuthError(err: any): string {
    const code = err.code || '';
    if (code === 'auth/invalid-credential' || code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-email') {
      return 'ACCESO DENEGADO: CREDENCIALES INVÁLIDAS';
    }
    if (code === 'auth/too-many-requests') {
      return 'SISTEMA BLOQUEADO: DEMASIADOS INTENTOS FALLIDOS. ESPERE.';
    }
    if (code === 'auth/network-request-failed') {
      return 'ERROR DE ENLACE: VERIFIQUE CONEXIÓN A LA RED';
    }
    if (code === 'auth/user-disabled') {
      return 'ACCESO DENEGADO: CUENTA DESHABILITADA';
    }
    return 'ERROR CRÍTICO DE AUTENTICACIÓN: ' + (code || 'DESCONOCIDO');
  }
}
